import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import MainLayout from '../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../shell/backofficeSidebar';
import { getLocalized, workspaceDocArchitecture, workspaceDocs } from '../docsContent';

function WorkspaceDocumentationPage() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { docId } = useParams<{ docId?: string }>();
  const sidebarItems = getBackofficeSidebarItems(t);

  const [query, setQuery] = React.useState('');
  const docs = React.useMemo(
    () => workspaceDocs.filter((item) => item.family === 'functional'),
    [],
  );

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return docs;
    return docs.filter((item) => {
      const title = getLocalized(item.title, i18n.language).toLowerCase();
      const section = getLocalized(item.section, i18n.language).toLowerCase();
      const objective = getLocalized(item.objective, i18n.language).toLowerCase();
      const keys = item.keywords.join(' ').toLowerCase();
      return title.includes(q) || section.includes(q) || objective.includes(q) || keys.includes(q);
    });
  }, [docs, i18n.language, query]);

  const bySection = React.useMemo(() => {
    const map = new Map<string, typeof filtered>();
    for (const item of filtered) {
      const key = getLocalized(item.section, i18n.language);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    }
    return Array.from(map.entries());
  }, [filtered, i18n.language]);

  const selectedDoc = React.useMemo(
    () => docs.find((item) => item.id === docId) ?? null,
    [docId, docs],
  );

  const [openSections, setOpenSections] = React.useState<Record<string, boolean>>({});
  const [openItems, setOpenItems] = React.useState<Record<string, boolean>>({});

  React.useEffect(() => {
    setOpenSections((prev) => {
      const next = { ...prev };
      for (const [section] of bySection) {
        if (next[section] === undefined) next[section] = false;
      }
      return next;
    });
  }, [bySection]);

  React.useEffect(() => {
    if (!selectedDoc) return;
    const selectedSection = bySection.find(([, items]) => items.some((item) => item.id === selectedDoc.id))?.[0];
    if (selectedSection) {
      setOpenSections((prev) => ({ ...prev, [selectedSection]: true }));
    }
    setOpenItems((prev) => ({ ...prev, [selectedDoc.id]: true }));
  }, [bySection, selectedDoc]);

  React.useEffect(() => {
    if (docId) return;
    const firstDoc = filtered[0] ?? docs[0];
    if (!firstDoc) return;
    navigate(`/workspace/docs/${firstDoc.id}`, { replace: true });
  }, [docId, docs, filtered, navigate]);

  return (
    <MainLayout withSidebar sidebarItems={sidebarItems} activePath={location.pathname}>
      <main className="flex h-[calc(100vh-5rem)] flex-1 flex-col gap-4 overflow-hidden pb-4">
        <section className="z-20 flex-none rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">
            {getLocalized(workspaceDocArchitecture.title, i18n.language)}
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            {getLocalized(workspaceDocArchitecture.subtitle, i18n.language)}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled
              className="cursor-default rounded-full bg-brand-600 px-4 py-2 text-xs font-semibold text-white"
            >
              {getLocalized(workspaceDocArchitecture.families[0].title, i18n.language)}
            </button>
          </div>
          <div className="mt-4">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('docs_search_placeholder', {
                defaultValue: 'Search documentation...',
              })}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
            />
          </div>
        </section>

        <section className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[minmax(240px,0.8fr)_minmax(0,2fr)]">
          <aside className="h-full rounded-2xl border border-slate-200 bg-white p-4">
            <h2 className="mb-3 text-sm font-semibold text-slate-900">
              {t('docs_menu_title', { defaultValue: 'Documentation menu' })}
            </h2>
            <div className="space-y-3">
              {bySection.map(([section, items]) => (
                <div key={section}>
                  <button
                    type="button"
                    onClick={() => setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }))}
                    className="mb-1 flex w-full items-center justify-between rounded px-1 py-1 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 hover:bg-slate-50"
                  >
                    <span>{section}</span>
                    <span>{openSections[section] ? '−' : '+'}</span>
                  </button>
                  {openSections[section] ? (
                    <ul className="space-y-1 text-xs text-slate-700">
                      {items.map((item) => {
                        const isSelected = item.id === selectedDoc?.id;
                        const itemTitle = getLocalized(item.title, i18n.language);
                        const isItemOpen = openItems[item.id] || isSelected;
                        return (
                          <li key={item.id} className={`rounded border ${isSelected ? 'border-brand-200' : 'border-slate-200'}`}>
                            <button
                              type="button"
                              onClick={() => setOpenItems((prev) => ({ ...prev, [item.id]: !isItemOpen }))}
                              className={`flex w-full items-center justify-between rounded px-2 py-1.5 text-left transition ${
                                isSelected
                                  ? 'bg-brand-50 text-brand-800'
                                  : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                              }`}
                            >
                              <span>{itemTitle}</span>
                              <span>{isItemOpen ? '−' : '+'}</span>
                            </button>
                            {isItemOpen ? (
                              <div className="space-y-2 border-t border-slate-200 bg-white px-2 py-2">
                                {item.route ? (
                                  <p className="text-[11px] text-slate-500">{item.route}</p>
                                ) : null}
                                <button
                                  type="button"
                                  onClick={() => navigate(`/workspace/docs/${item.id}`)}
                                  className={`w-full rounded px-2 py-1.5 text-left text-[11px] font-semibold transition ${
                                    isSelected
                                      ? 'bg-brand-600 text-white hover:bg-brand-700'
                                      : 'bg-slate-900 text-white hover:bg-slate-800'
                                  }`}
                                >
                                  {t('docs_open_article', { defaultValue: 'Open article' })}
                                </button>
                              </div>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </div>
              ))}
            </div>
          </aside>

          <div className="h-full overflow-y-auto pr-1">
            {selectedDoc ? (
              <article className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700">
                    {getLocalized(selectedDoc.section, i18n.language)}
                  </span>
                  {selectedDoc.route ? (
                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                      {selectedDoc.route}
                    </span>
                  ) : null}
                </div>
                <h3 className="mt-2 text-lg font-semibold text-slate-900">
                  {getLocalized(selectedDoc.title, i18n.language)}
                </h3>
                <p className="mt-1 text-xs text-slate-500">
                  <span className="font-semibold">
                    {t('docs_audience_label', { defaultValue: 'Audience:' })}
                  </span>{' '}
                  {getLocalized(selectedDoc.audience, i18n.language)}
                </p>
                <p className="mt-3 text-sm text-slate-700">
                  {getLocalized(selectedDoc.objective, i18n.language)}
                </p>
                <div className="mt-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {t('docs_procedure', { defaultValue: 'Procedure' })}
                  </p>
                  <ol className="mt-1 list-decimal space-y-1 pl-5 text-sm text-slate-700">
                    {selectedDoc.steps.map((step, idx) => (
                      <li key={`${selectedDoc.id}-step-${idx}`}>{getLocalized(step, i18n.language)}</li>
                    ))}
                  </ol>
                </div>
                {selectedDoc.buttons.length > 0 ? (
                  <div className="mt-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {t('docs_button_reference', {
                        defaultValue: 'Button reference',
                      })}
                    </p>
                    <div className="mt-2 overflow-x-auto">
                      <table className="min-w-full text-left text-xs">
                        <thead>
                          <tr className="border-b border-slate-200 text-slate-500">
                            <th className="px-2 py-1.5">
                              {t('docs_table_button', { defaultValue: 'Button' })}
                            </th>
                            <th className="px-2 py-1.5">
                              {t('docs_table_action', { defaultValue: 'Action' })}
                            </th>
                            <th className="px-2 py-1.5">
                              {t('docs_table_result', { defaultValue: 'Result' })}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedDoc.buttons.map((btn) => (
                            <tr key={btn.id} className="border-b border-slate-100 text-slate-700">
                              <td className="px-2 py-1.5 font-semibold">{getLocalized(btn.label, i18n.language)}</td>
                              <td className="px-2 py-1.5">{getLocalized(btn.action, i18n.language)}</td>
                              <td className="px-2 py-1.5">{getLocalized(btn.outcome, i18n.language)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}
              </article>
            ) : (
              <article className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
                {t('docs_select_item_hint', {
                  defaultValue: 'Select a documentation item from the menu.',
                })}
              </article>
            )}
          </div>
        </section>
      </main>
    </MainLayout>
  );
}

export default WorkspaceDocumentationPage;
